import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { get } from "@/lib/api";
import { useTree } from "@/lib/queries";
import type { LibraryItem } from "@/lib/types";
import { Input } from "@/components/ui/input";
import { KindIcon, kindLabel } from "@/features/notes/kinds";
import { cn } from "@/lib/utils";

/** Searchable list of notes/records in a workspace, optionally restricted to kinds. */
export function NotePicker(props: {
  workspaceId: string;
  kinds?: string[];
  excludeIds?: string[];
  selected?: string[];
  multiple?: boolean;
  onPick(id: string, title: string): void;
  label?: string;
  maxHeight?: string;
}) {
  const tree = useTree(props.workspaceId);
  const [q, setQ] = useState("");
  const items = useMemo(
    () =>
      (tree.data?.notes ?? [])
        .filter((n) => (!props.kinds || props.kinds.includes(n.kind)) && !props.excludeIds?.includes(n.id))
        .filter((n) => !q || n.title.toLowerCase().includes(q.toLowerCase()))
        .slice(0, 80),
    [tree.data, props.kinds, props.excludeIds, q],
  );
  return (
    <div className="space-y-1.5">
      <Input placeholder={props.label ?? "Filter records…"} aria-label={props.label ?? "Filter records"} value={q} onChange={(e) => setQ(e.target.value)} className="h-8" />
      <ul className="space-y-0.5 overflow-y-auto rounded-md border p-1" style={{ maxHeight: props.maxHeight ?? "12rem" }}>
        {items.map((n) => {
          const sel = props.selected?.includes(n.id);
          return (
            <li key={n.id}>
              <button
                type="button"
                aria-pressed={props.multiple ? sel : undefined}
                onClick={() => props.onPick(n.id, n.title)}
                className={cn("flex w-full items-center gap-1.5 rounded px-2 py-1 text-left text-sm hover:bg-accent", sel && "bg-accent font-medium")}
              >
                <KindIcon kind={n.kind} />
                <span className="min-w-0 flex-1 truncate">{n.title}</span>
                <span className="shrink-0 text-xs text-muted-foreground">{kindLabel(n.kind)}</span>
                {sel && <span className="text-xs">✓</span>}
              </button>
            </li>
          );
        })}
        {tree.data && !items.length && <li className="px-2 py-1 text-sm text-muted-foreground">No matching records.</li>}
      </ul>
    </div>
  );
}

export function useLibrary(workspaceId: string) {
  return useQuery({ queryKey: ["library", workspaceId], queryFn: () => get<{ items: LibraryItem[] }>(`/api/workspaces/${workspaceId}/library`) });
}

export function LibraryPicker(props: { workspaceId: string; onPick(item: LibraryItem): void; selectedId?: string | null }) {
  const lib = useLibrary(props.workspaceId);
  const [q, setQ] = useState("");
  const items = (lib.data?.items ?? []).filter((i) => !q || `${i.title} ${i.citationKey ?? ""}`.toLowerCase().includes(q.toLowerCase())).slice(0, 60);
  return (
    <div className="space-y-1.5">
      <Input placeholder="Filter library…" aria-label="Filter library" value={q} onChange={(e) => setQ(e.target.value)} className="h-8" />
      <ul className="max-h-48 space-y-0.5 overflow-y-auto rounded-md border p-1">
        {items.map((i) => (
          <li key={i.id}>
            <button
              type="button"
              onClick={() => props.onPick(i)}
              className={cn("w-full rounded px-2 py-1 text-left text-sm hover:bg-accent", props.selectedId === i.id && "bg-accent font-medium")}
            >
              <span className="block truncate">{i.title || "Untitled source"}</span>
              <span className="block text-xs text-muted-foreground">
                {i.citationKey ? `@${i.citationKey}` : "no key"} · {i.year ?? "year not recorded"} · {i.attachments?.some((a) => a.mime === "application/pdf") ? "PDF" : "no PDF"}
              </span>
            </button>
          </li>
        ))}
        {lib.data && !items.length && <li className="px-2 py-1 text-sm text-muted-foreground">No library records.</li>}
      </ul>
    </div>
  );
}
